import {
  Assignment,
  Campaign as CampaignIcon,
  Category,
  CheckCircle,
  Check as CheckIcon,
  TextFields as TextFieldsIcon,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useAccount, useChat, useToast } from "../../../context";
import { CampaignTemplate, Template } from "../../../interfaces/all";
import {
  getCampaignsWithTemplates,
  getTemplateParameters,
  getTemplates,
} from "../../../services/api";
import { apiService } from "../../../services/axios";
import { ParameterConfig } from "../../../types/template-config";

const TemplateDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
    background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
    border: "1px solid rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(10px)",
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflow: "hidden",
    animation: "modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "@keyframes modalSlideIn": {
      "0%": {
        opacity: 0,
        transform: "scale(0.9) translateY(-20px)",
      },
      "100%": {
        opacity: 1,
        transform: "scale(1) translateY(0)",
      },
    },
  },
});

interface TemplateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (
    template: CampaignTemplate & {
      parameterConfigs?: ParameterConfig[];
      templateParameters?: any;
    },
  ) => void;
  campaignId?: string;
  initialTemplateId?: string;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  campaignId,
  initialTemplateId,
}) => {
  const { showError } = useToast();
  const { account } = useAccount();
  const { selectedConversation } = useChat();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [campaignTemplates, setCampaignTemplates] = useState<
    CampaignTemplate[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );
  const [templateParameters, setTemplateParameters] = useState<any>(null);
  const [parameterConfigs, setParameterConfigs] = useState<ParameterConfig[]>(
    [],
  );
  const [loadingParameters, setLoadingParameters] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [retryKey, setRetryKey] = useState(0);

  const fetchUserTemplates = useCallback(async () => {
    if (!account?.company_id) {
      setError("ID da empresa não encontrado");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [templatesResponse, campaignsResponse] = await Promise.all([
        getTemplates(account.company_id),
        getCampaignsWithTemplates(),
      ]);

      if (
        templatesResponse.status === "success" &&
        templatesResponse.data?.data
      ) {
        setTemplates(templatesResponse.data.data as unknown as Template[]);
      }

      if (campaignsResponse.status === "success" && campaignsResponse.data) {
        setCampaignTemplates(
          campaignsResponse.data as unknown as CampaignTemplate[],
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Erro desconhecido";
      setError(errorMessage);
      showError(err);
    } finally {
      setLoading(false);
    }
  }, [account?.company_id, showError]);

  // Função para obter valores reais da conversa atual
  const getRealConversationValues = useCallback(() => {
    if (!selectedConversation || !account) {
      return {
        clientName: "[Nome do Cliente]",
        clientPhone: "[Telefone do Cliente]",
        companyName: "[Nome da Empresa]",
        attendantName: "[Nome do Atendente]",
        scheduledDate: "[Data do Agendamento]",
      };
    }

    return {
      clientName:
        selectedConversation.valid_name ||
        selectedConversation.name ||
        "[Nome do Cliente]",
      clientPhone:
        selectedConversation.valid_telephone ||
        selectedConversation.telephone ||
        "[Telefone do Cliente]",
      companyName: account.company?.name || "[Nome da Empresa]",
      attendantName:
        selectedConversation.attendant_name ||
        account.name ||
        "[Nome do Atendente]",
      scheduledDate: "[Data do Agendamento]", // Este seria calculado baseado em agendamentos futuros
    };
  }, [selectedConversation, account]);

  // Função para gerar preview com valores reais
  const generateRealTemplatePreview = useCallback(
    (template: Template, configs: ParameterConfig[]) => {
      if (!template.components) return null;

      const preview = {
        header: null as any,
        body: null as any,
        footer: null as any,
        buttons: [] as any[],
      };

      template.components.forEach((component: any) => {
        switch (component.type) {
          case "HEADER": {
            if (component.format === "TEXT") {
              let headerText = component.text ?? "";

              configs.forEach((config, index) => {
                const regex = new RegExp(`\\{\\{${index + 1}\\}\\}`, "g");
                headerText = headerText.replace(regex, config.value);
              });

              preview.header = { type: "text", text: headerText };
            } else if (component.format === "IMAGE") {
              preview.header = {
                type: "image",
                imageUrl: component.example?.header_handle?.[0] ?? null,
                text: component.text ?? "",
              };
            }
            break;
          }

          case "BODY": {
            let bodyText = component.text ?? "";

            configs.forEach((config, index) => {
              const regex = new RegExp(`\\{\\{${index + 1}\\}\\}`, "g");
              bodyText = bodyText.replace(regex, config.value);
            });

            preview.body = { text: bodyText };
            break;
          }

          case "FOOTER": {
            preview.footer = { text: component.text ?? "" };
            break;
          }

          case "BUTTONS": {
            if (component.buttons) {
              preview.buttons = component.buttons.map((button: any) => ({
                type: button.type,
                text: button.text,
              }));
            }
            break;
          }
        }
      });

      return preview;
    },
    [],
  );

  // Carregar configuração pré-definida do template
  const loadPredefinedTemplateConfig = async (templateId: string) => {
    if (!account?.company_id) {
      return null;
    }

    try {
      const response = await apiService.get(
        `/template-configs/${templateId}?companyId=${account.company_id}`,
      );
      if (
        response.status === "success" &&
        response.data &&
        typeof response.data === "object" &&
        response.data !== null &&
        "parameters" in response.data
      ) {
        return (response.data as { parameters: any[] }).parameters || [];
      }
    } catch (error) {
      // Ignorar erro se não houver configuração pré-definida
    }
    return null;
  };

  useEffect(() => {
    if (isOpen) {
      fetchUserTemplates();
    }
  }, [isOpen, retryKey, fetchUserTemplates]);

  useEffect(() => {
    if (
      isOpen &&
      templates.length > 0 &&
      initialTemplateId &&
      !selectedTemplate
    ) {
      const matched = templates.find(
        (t) => t.id === initialTemplateId || t.name === initialTemplateId,
      );
      if (matched) {
        handleTemplateClick(matched);
      }
    }
  }, [isOpen, templates, initialTemplateId, selectedTemplate]);

  const handleSendTemplate = () => {
    if (selectedTemplate) {
      // Encontrar o CampaignTemplate correspondente
      const campaignTemplate = campaignTemplates.find(
        (ct) =>
          ct.template_id === selectedTemplate.id &&
          (!campaignId || ct.campaign_id === campaignId),
      );

      if (campaignTemplate) {
        // Criar um objeto estendido com os parameterConfigs
        const templateWithConfigs = {
          ...campaignTemplate,
          parameterConfigs: parameterConfigs,
          templateParameters: templateParameters,
        };

        onSelectTemplate(templateWithConfigs);
        onClose();
      }
    }
  };

  const handleTemplateClick = async (template: Template) => {
    setSelectedTemplate(template);

    try {
      setLoadingParameters(true);

      // Obter valores reais da conversa atual
      const realValues = getRealConversationValues();

      // Carregar configuração pré-definida
      const predefinedConfig = await loadPredefinedTemplateConfig(template.id);

      const response = await getTemplateParameters(
        template.id,
        template.name,
        account?.company_id,
      );

      if (response.status === "success" && response.data) {
        setTemplateParameters(response.data);

        // Se há configuração pré-definida, aplicar ela
        if (predefinedConfig && predefinedConfig.length > 0) {
          const configs: ParameterConfig[] = [];
          for (let i = 0; i < predefinedConfig.length; i++) {
            const param = predefinedConfig[i];
            let type: "fixed" | "dynamic" = "dynamic";
            let value = "";
            let dynamicField: ParameterConfig["dynamicField"] = "clientName";

            // Se param é um objeto (estrutura salva)
            if (typeof param === "object" && param !== null) {
              type = param.type || "dynamic";
              value = param.value || "";
              dynamicField = param.dynamicField || "clientName";
            } else {
              // Se param é uma string simples, tratar como valor fixo
              type = "fixed";
              value = String(param);
            }

            // Se é dinâmico, usar valor real da conversa
            if (type === "dynamic") {
              switch (dynamicField) {
                case "clientName":
                  value = realValues.clientName;
                  break;
                case "clientPhone":
                  value = realValues.clientPhone;
                  break;
                case "companyName":
                  value = realValues.companyName;
                  break;
                case "attendantName":
                  value = realValues.attendantName;
                  break;
                case "scheduledDate":
                  value = realValues.scheduledDate;
                  break;
                default:
                  value = realValues.clientName;
              }
            }

            configs.push({
              index: i,
              type,
              value,
              dynamicField,
            });
          }
          setParameterConfigs(configs);
        } else {
          // Se não há configuração pré-definida, usar valores reais da conversa
          const configs: ParameterConfig[] = [];
          const templateData = response.data as any;
          if (templateData.requiredParametersCount > 0) {
            // Mapear cada parâmetro para o campo correto baseado na posição
            const fieldMapping = [
              "clientName",
              "attendantName",
              "companyName",
              "scheduledDate",
            ];
            const valueMapping = [
              realValues.clientName,
              realValues.attendantName,
              realValues.companyName,
              realValues.scheduledDate,
            ];

            for (let i = 0; i < templateData.requiredParametersCount; i++) {
              const field = fieldMapping[i] || "clientName";
              const value = valueMapping[i] || realValues.clientName;

              configs.push({
                index: i,
                type: "dynamic",
                value: value,
                dynamicField: field as ParameterConfig["dynamicField"],
              });
            }
          }
          setParameterConfigs(configs);
        }
      }
    } catch (err) {
      console.error("Erro ao carregar parâmetros do template:", err);
      setTemplateParameters(null);
      setParameterConfigs([]);
    } finally {
      setLoadingParameters(false);
    }
  };

  // Agrupar templates por campanha, filtrando apenas pela campanha específica se fornecida
  const groupedTemplates = campaignTemplates.reduce(
    (groups, campaignTemplate) => {
      // Se campaignId foi fornecido, filtrar apenas templates dessa campanha
      if (campaignId && campaignTemplate.campaign_id !== campaignId) {
        return groups;
      }

      const campaignName = campaignTemplate.campaign_name;
      if (!groups[campaignName]) {
        groups[campaignName] = [];
      }
      groups[campaignName].push(campaignTemplate);
      return groups;
    },
    {} as Record<string, CampaignTemplate[]>,
  );

  return (
    <TemplateDialog
      open={isOpen}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle
        sx={{
          p: 3,
          borderBottom: "1px solid #e9edef",
          background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
          position: "relative",
        }}
      >
        {/* Elemento decorativo sutil */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 120,
            height: 120,
            background:
              "radial-gradient(circle, rgba(0, 128, 105, 0.05) 0%, transparent 70%)",
            borderRadius: "50%",
            transform: "translate(30px, -30px)",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Assignment sx={{ color: "#008069", fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#111b21" }}>
            Selecionar Template
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          minHeight: 400,
          maxHeight: isMobile ? "80vh" : 600,
          p: 0,
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            height: "100%",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          {/* Coluna da Esquerda - Lista de Templates */}
          <Box
            sx={{
              flex: isMobile ? "none" : 1,
              borderRight: isMobile ? "none" : "1px solid #e9edef",
              height: isMobile ? "auto" : "100%",
              maxHeight: isMobile ? "300px" : "none",
              overflowY: "auto",
              minWidth: 0,
            }}
          >
            <Box sx={{ p: 3 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
              >
                <CampaignIcon sx={{ color: "#00a884", fontSize: 18 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#111b21", fontSize: "14px" }}
                >
                  Selecionar Template
                </Typography>
                {selectedTemplate && (
                  <Chip
                    icon={<CheckIcon sx={{ fontSize: 12 }} />}
                    label="Selecionado"
                    size="small"
                    color="success"
                    sx={{ fontSize: "10px", height: "20px" }}
                  />
                )}
              </Box>

              {loading ? (
                <Card
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid #e9edef",
                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                    p: 3,
                    textAlign: "center",
                  }}
                >
                  <CircularProgress
                    size={24}
                    sx={{ color: "#00a884", mb: 2 }}
                  />
                  <Typography variant="body2" sx={{ color: "#667781" }}>
                    Carregando templates...
                  </Typography>
                </Card>
              ) : error ? (
                <Card
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid #ffcdd2",
                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                    p: 3,
                    backgroundColor: "#ffebee",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#c62828", textAlign: "center", mb: 2 }}
                  >
                    ⚠️ {error}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setRetryKey((k) => k + 1)}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 500,
                      borderColor: "#00a884",
                      color: "#00a884",
                      fontSize: "12px",
                      "&:hover": {
                        borderColor: "#006b5a",
                        backgroundColor: "rgba(0, 168, 132, 0.04)",
                      },
                    }}
                  >
                    Tentar novamente
                  </Button>
                </Card>
              ) : Object.keys(groupedTemplates).length === 0 ? (
                <Card
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid #e9edef",
                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                    p: 3,
                    textAlign: "center",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#667781", fontStyle: "italic", mb: 1 }}
                  >
                    📋 Nenhum template disponível para esta campanha
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#8e8e8e", display: "block" }}
                  >
                    {campaignId
                      ? "Esta campanha não possui templates atribuídos."
                      : "Nenhum template encontrado."}
                  </Typography>
                </Card>
              ) : (
                <Card
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid #e9edef",
                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                    maxHeight: "400px",
                    overflow: "auto",
                  }}
                >
                  {Object.entries(groupedTemplates).map(
                    ([campaignName, campaignTemplates]) => (
                      <Box key={campaignName} sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1.5,
                            px: 2,
                            pt: 2,
                          }}
                        >
                          <Category
                            sx={{ fontSize: 16, color: "#00a884", mr: 1 }}
                          />
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: "#00a884",
                              fontSize: "13px",
                            }}
                          >
                            {campaignName}
                          </Typography>
                        </Box>
                        <List sx={{ p: 0 }}>
                          {campaignTemplates.map((campaignTemplate) => {
                            const template = templates.find(
                              (t) => t.id === campaignTemplate.template_id,
                            );
                            if (!template) return null;

                            return (
                              <ListItem
                                key={`${campaignTemplate.campaign_id}-${campaignTemplate.template_id}`}
                                onClick={() => handleTemplateClick(template)}
                                sx={{
                                  cursor: "pointer",
                                  borderBottom: "1px solid #f0f0f0",
                                  backgroundColor:
                                    selectedTemplate?.id === template.id
                                      ? "#e3f2fd"
                                      : "transparent",
                                  borderLeft:
                                    selectedTemplate?.id === template.id
                                      ? "4px solid #2196f3"
                                      : "4px solid transparent",
                                  "&:hover": {
                                    backgroundColor: "#f5f6f6",
                                  },
                                  "&:last-child": {
                                    borderBottom: "none",
                                  },
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#111b21",
                                      mb: 0.5,
                                    }}
                                  >
                                    {template.name}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Chip
                                      label={template.category || "Geral"}
                                      size="small"
                                      sx={{ fontSize: "10px", height: "20px" }}
                                    />
                                    <Chip
                                      label={template.language || "pt"}
                                      size="small"
                                      sx={{ fontSize: "10px", height: "20px" }}
                                    />
                                    <Chip
                                      label={
                                        template.status === "APPROVED"
                                          ? "Aprovado"
                                          : "Pendente"
                                      }
                                      size="small"
                                      color={
                                        template.status === "APPROVED"
                                          ? "success"
                                          : "warning"
                                      }
                                      sx={{ fontSize: "10px", height: "20px" }}
                                    />
                                  </Box>
                                </Box>
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    ),
                  )}
                </Card>
              )}
            </Box>
          </Box>

          {/* Coluna da Direita - Preview */}
          <Box
            sx={{
              flex: isMobile ? "none" : 1,
              height: isMobile ? "auto" : "100%",
              background: "#f8f9fa",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
              >
                <TextFieldsIcon sx={{ color: "#00a884", fontSize: 18 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#111b21", fontSize: "14px" }}
                >
                  Preview do Template
                </Typography>
                {selectedTemplate && (
                  <Chip
                    icon={<CheckCircle sx={{ fontSize: 12 }} />}
                    label="Pronto para Envio"
                    size="small"
                    color="success"
                    sx={{ fontSize: "10px", height: "20px" }}
                  />
                )}
              </Box>

              {loadingParameters ? (
                <Card
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid #e9edef",
                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                    p: 3,
                    textAlign: "center",
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="body2" sx={{ color: "#667781" }}>
                      Carregando parâmetros do template...
                    </Typography>
                  </Box>
                </Card>
              ) : selectedTemplate && templateParameters ? (
                <Card
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid #e9edef",
                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                    flex: 1,
                    p: 2,
                  }}
                >
                  {(() => {
                    const preview = generateRealTemplatePreview(
                      selectedTemplate,
                      parameterConfigs,
                    );
                    if (!preview) {
                      return (
                        <Box sx={{ p: 2, textAlign: "center" }}>
                          <Typography variant="body2" sx={{ color: "#667781" }}>
                            Template sem componentes definidos
                          </Typography>
                        </Box>
                      );
                    }

                    return (
                      <Box
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: "16px",
                          border: "1px solid #e0e0e0",
                          overflow: "hidden",
                          maxWidth: "350px",
                          mx: "auto",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                      >
                        {/* Header */}
                        {preview.header && (
                          <Box sx={{ borderBottom: "1px solid #f0f0f0" }}>
                            {preview.header.type === "image" ? (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: "200px",
                                  backgroundColor: "#f5f5f5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  position: "relative",
                                  overflow: "hidden",
                                }}
                              >
                                {preview.header.imageUrl ? (
                                  <img
                                    src={preview.header.imageUrl}
                                    alt="Header do Template"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <Box sx={{ textAlign: "center", p: 2 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#666", mb: 1 }}
                                    >
                                      📷 Imagem do Header
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "#999" }}
                                    >
                                      {preview.header.text ||
                                        "Template com imagem no header"}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <Box sx={{ p: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    whiteSpace: "pre-line",
                                  }}
                                >
                                  {preview.header.text}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Body */}
                        {preview.body && (
                          <Box sx={{ p: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ lineHeight: 1.6, whiteSpace: "pre-line" }}
                            >
                              {preview.body.text}
                            </Typography>
                          </Box>
                        )}

                        {/* Footer */}
                        {preview.footer && (
                          <Box
                            sx={{
                              p: 2,
                              borderTop: "1px solid #f0f0f0",
                              backgroundColor: "#f9f9f9",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#666", whiteSpace: "pre-line" }}
                            >
                              {preview.footer.text}
                            </Typography>
                          </Box>
                        )}

                        {/* Buttons */}
                        {preview.buttons && preview.buttons.length > 0 && (
                          <Box sx={{ p: 2, borderTop: "1px solid #f0f0f0" }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              {preview.buttons.map(
                                (button: any, index: number) => (
                                  <Button
                                    key={index}
                                    variant={
                                      button.type === "URL"
                                        ? "outlined"
                                        : "contained"
                                    }
                                    size="small"
                                    sx={{
                                      textTransform: "none",
                                      fontSize: "12px",
                                      backgroundColor:
                                        button.type === "URL"
                                          ? "transparent"
                                          : "#25d366",
                                      color:
                                        button.type === "URL"
                                          ? "#25d366"
                                          : "#fff",
                                      borderColor: "#25d366",
                                      "&:hover": {
                                        backgroundColor:
                                          button.type === "URL"
                                            ? "#f0f0f0"
                                            : "#20c05a",
                                        borderColor: "#25d366",
                                        color:
                                          button.type === "URL"
                                            ? "#25d366"
                                            : "#fff",
                                      },
                                    }}
                                  >
                                    {button.text}
                                  </Button>
                                ),
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </Card>
              ) : (
                <Card
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid #e9edef",
                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                    p: 3,
                    textAlign: "center",
                    backgroundColor: "#f8f9fa",
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Visibility
                      sx={{ color: "#667781", fontSize: 48, mb: 2 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#667781",
                        fontStyle: "italic",
                      }}
                    >
                      Selecione um template para ver o preview
                    </Typography>
                  </Box>
                </Card>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "space-between",
          p: 3,
          borderTop: "1px solid #e9edef",
          background: "#f8f9fa",
        }}
      >
        <Button
          onClick={onClose}
          size="medium"
          variant="outlined"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            borderColor: "#667781",
            color: "#667781",
            "&:hover": {
              borderColor: "#54656f",
              backgroundColor: "rgba(102, 119, 129, 0.04)",
            },
          }}
        >
          Cancelar
        </Button>

        <Button
          onClick={handleSendTemplate}
          disabled={!selectedTemplate}
          size="medium"
          variant="contained"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            backgroundColor: "#008069",
            "&:hover": {
              backgroundColor: "#006b5a",
            },
            "&:disabled": {
              backgroundColor: "#e0e0e0",
              color: "#9e9e9e",
            },
          }}
        >
          Enviar Template
        </Button>
      </DialogActions>
    </TemplateDialog>
  );
};

export default TemplateSelector;
